import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { debounce } from "lodash";
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import '../App.css';
import { useApiMutation } from "../hooks/useApi";
import TALogo from "../logo/TALogo.svg";
import { Registration } from "../types/RegistrationDetails";

interface Props {
  setNextClicked: Dispatch<SetStateAction<boolean>>;
  nextClicked: boolean;
  registrationData: Registration;
}

type FormData = {
  password: string;
  confirmPassword: string;
  email: string;
  name: string;
  clientName: string;
  fullAddress: string;
};

const RegisterBox: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { nextClicked, setNextClicked, registrationData } = props;

  const { apiUpdate } = useApiMutation();

  const [searchParams] = useSearchParams();
  const [currentId] = useState<string | null>(searchParams.get("id"));

  const [name] = useState<string>(registrationData.name);
  const [email] = useState<string>(registrationData.email);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    getValues,
    trigger
  } = useForm<FormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
      email: registrationData.email,
      name: registrationData.name,
      clientName: registrationData.clientName,
      fullAddress: registrationData.fullAddress,
    },
  });

  const Navigate = useNavigate();

  const onSubmit: SubmitHandler<FormData> = (data) => {
    setNextClicked(true);
  };

  const onSubmit2: SubmitHandler<FormData> = (data) => {
    apiUpdate.mutate(
      {
        entity: "register",
        post: {
          id: currentId,
          name: data.name,
          // clientName: data.clientName,
          // fullAddress: data.fullAddress,
          // email: data.email,
          password: data.password,
        },
      },
      { onSettled: () => Navigate("/") }
    );
  };

  const isPasswordInName = (password: string, name: string, email: string) => {
    if (!password) {
      return false;
    }

    const splitName: string[] = [...name.split(" "), ...email.split("@")];

    for (let word of splitName) {
      for (let i = 0; i < word.length - 2; i++) {
        let substring: string = word.substring(i, i + 3);
        if (password.toLowerCase().includes(substring.toLowerCase())) {
          return true;
        }
      }
    }

    return false;
  };

  const [requirementList, setRequirementList] = useState<(boolean | void)[]>([
    false,
    false,
    false,
  ]);
  const [subRequirements, setSubRequirements] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  const checkIfTrue = (conditionArray: boolean[]) => {
    let count = 0;
    for (let i = 0; i < conditionArray.length; i++) {
      if (conditionArray[i]) {
        count++;
      }
    }
    return count >= 3;
  };

  const getPasswordRequirements = (password: string) => {
    const conditionArray = [
      password ? /[A-Z]/.test(password) : false,
      password ? /[a-z]/.test(password) : false,
      password ? /[0-9]/.test(password) : false,
      password ? /[!@#$%^&*]/.test(password) : false,
    ];

    const requirements = [
      password ? password.length >= 12 : false,
      password ? !isPasswordInName(password, name, email)! : false,
      password ? checkIfTrue(conditionArray) : false,
    ];

    setRequirementList(requirements);
    setSubRequirements(conditionArray);

    return requirements.every((rl) => rl);
  };

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const debouncedTriggerValidation = debounce(() => {
    trigger("confirmPassword");
  }, 1500);


  useEffect(() => {
    if (confirmPassword) {
      debouncedTriggerValidation();
    }
  }, [password, confirmPassword, trigger]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const { t } = useTranslation();

  return (
    <Container
      component={Paper}
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        overflow: isSmallScreen ? "scroll" : "hidden",
        width: isSmallScreen ? "100%" : "40%",
        padding: isSmallScreen ? "50px 15px 100px 15px" : "30px",
        height: isSmallScreen ? "100%" : "auto",
      }}
    >
      <Stack padding={2} spacing={4} textAlign={"center"}>
        <Box>
          <Box component={"img"} src={TALogo} alt="Logo" sx={{ width: isSmallScreen ? "90%" : "auto" }} />
        </Box>

        {/* New Changes    */}
        <Typography variant="h6"> {t("registration.welcome")} </Typography>

        <Typography variant="body1">
          {t("registration.invitationMessage")}
        </Typography>

        <Stack
          spacing={4}
          textAlign={"center"}
          component="form"
          onSubmit={handleSubmit(onSubmit2)}
        >

          <Stack
            direction={isSmallScreen ? "column" : "row"}
            spacing={4}
            width={"100%"}
            alignContent={"space-between"}
          >

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("registration.email")}
                  fullWidth
                  // InputProps={{
                  //   readOnly: true,
                  // }}
                  disabled
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("registration.fullName")} fullWidth />
              )}
            />
          </Stack>

          <Stack
            direction={isSmallScreen ? "column" : "row"}
            spacing={4}
            width={"100%"}
            alignContent={"space-between"}
          >
            <Controller
              name="password"
              control={control}
              rules={{
                validate: {
                  notInName: (value) => getPasswordRequirements(value),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("registration.password")}
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  onChange={(e) => {
                    setPassword(e.target.value);
                    field.onChange(e);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: t("registration.confirmPasswordRequired"),
                validate: (value) => {
                  return value === getValues("password") || t("registration.passwordMismatch");
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("registration.confirmPassword")}
                  type={showConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  fullWidth
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    field.onChange(e);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

          </Stack>

          <Box
            sx={{
              border: "1px rgba(0,0,0,0.23) solid",
              borderRadius: "4px",
              p: "10px",
              display: "flex",
              alignItems: "start",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" textAlign={"start"}>
              {t("registration.passwordReqTitle")}
            </Typography>
            <List sx={{ lineHeight: "30px" }}>
              <ListItem sx={{ color: requirementList[0] ? "#2E7D32" : "#D32F2F" }}>
                {requirementList[0] ? (
                  <ListItemIcon>
                    <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                  </ListItemIcon>
                ) : (
                  <ListItemIcon>
                    <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={t("registration.minCharLength")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem sx={{ color: requirementList[1] ? "#2E7D32" : "#D32F2F" }}>
                {requirementList[1] ? (
                  <ListItemIcon>
                    <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                  </ListItemIcon>
                ) : (
                  <ListItemIcon>
                    <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={t("registration.notContainName")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem sx={{ color: requirementList[2] ? "#2E7D32" : "#D32F2F" }}>
                {requirementList[2] ? (
                  <ListItemIcon>
                    <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                  </ListItemIcon>
                ) : (
                  <ListItemIcon>
                    <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={t("registration.categories")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem
                sx={{
                  color: subRequirements[0] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                }}
              >
                <ListItemText
                  inset
                  secondary={t("registration.uppercaseReq")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem
                sx={{
                  color: subRequirements[1] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                }}
              >
                <ListItemText
                  inset
                  secondary={t("registration.lowercaseReq")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem
                sx={{
                  color: subRequirements[2] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                }}
              >
                <ListItemText
                  inset
                  secondary={t("registration.numberReq")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>

              <ListItem
                sx={{
                  color: subRequirements[3] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                }}
              >
                <ListItemText
                  inset
                  secondary={t("registration.symbolReq")}
                  sx={{ fontSize: "12px", lineHeight: "12px" }}
                  disableTypography
                />
              </ListItem>
            </List>
          </Box>

          <Typography variant="body2" textAlign={"left"}>{t("registration.redirectionText")} </Typography>

          <Button
            variant="contained"
            type="submit"
            disabled={!isValid}
          >
            {t("registration.register")}
          </Button>
        </Stack>


        {/* Old changes  */}
        {/* {nextClicked ? (
          <Typography variant="h6"> Your Details </Typography>
        ) : (
          <Typography variant="h6"> Welcome </Typography>
        )} */}

        {/* {nextClicked ? (
          <Typography variant="body1">
            {" "}
            Please confirm your details below.{" "}
          </Typography>
        ) : (
          <Typography variant="body1">
            You have been invited to the TeacherActive Timesheet system. Please
            create a password for your account.
          </Typography>
        )} */}

        {/* {!nextClicked && (
          <Stack
            spacing={4}
            textAlign={"center"}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  fullWidth
                  // InputProps={{
                  //   readOnly: true,
                  // }}
                  disabled
                />
              )}
            />

            <Stack
              direction={"row"}
              spacing={4}
              width={"100%"}
              alignContent={"space-between"}
            >
              <Controller
                name="password"
                control={control}
                rules={{
                  validate: {
                    notInName: (value) => getPasswordRequirements(value),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: "Confirm Password is required",
                  validate: (value) =>
                    value === getValues("password") ||
                    "The passwords do not match",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type="password"
                    variant="outlined"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Box
              sx={{
                border: "1px rgba(0,0,0,0.23) solid",
                borderRadius: "4px",
                p: "10px",
                display: "flex",
                alignItems: "start",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" textAlign={"start"}>
                {" "}
                Your password must:{" "}
              </Typography>
              <List sx={{ lineHeight: "30px" }}>
                <ListItem sx={{ color: requirementList[0] ? "#2E7D32" : "#D32F2F" }}>
                  {requirementList[0] ? (
                    <ListItemIcon>
                      <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon>
                      <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary="Be at least 12 characters long"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem sx={{ color: requirementList[1] ? "#2E7D32" : "#D32F2F" }}>
                  {requirementList[1] ? (
                    <ListItemIcon>
                      <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon>
                      <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary="Not contain the user's account name or parts of the user's full name that exceed two consecutive characters"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem sx={{ color: requirementList[2] ? "#2E7D32" : "#D32F2F" }}>
                  {requirementList[2] ? (
                    <ListItemIcon>
                      <DoneIcon fontSize="small" sx={{ color: "#2E7D32" }} />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon>
                      <CloseIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary="Contain characters from three of the following four categories:"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem
                  sx={{
                    color: subRequirements[0] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                  }}
                >
                  <ListItemText
                    inset
                    secondary="English uppercase characters (A through Z)"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem
                  sx={{
                    color: subRequirements[1] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                  }}
                >
                  <ListItemText
                    inset
                    secondary="English lowercase characters (a through z)"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem
                  sx={{
                    color: subRequirements[2] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                  }}
                >
                  <ListItemText
                    inset
                    secondary="Numbers (0 through 9)"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>

                <ListItem
                  sx={{
                    color: subRequirements[3] ? "#2E7D32" : "rgba(0,0,0,0.7)",
                  }}
                >
                  <ListItemText
                    inset
                    secondary="Non-alphabetic characters (! @ # $ % ^ & *)"
                    sx={{ fontSize: "12px", lineHeight: "12px" }}
                    disableTypography
                  />
                </ListItem>
              </List>
            </Box>

            <Button
              variant="contained"
              endIcon={
                <ChevronRightIcon
                  sx={{ color: !isValid ? "#BDBDBD" : "white" }}
                />
              }
              type="submit"
              disabled={!isValid}
            >
              Next
            </Button>
          </Stack>
        )}

        {nextClicked && (
          <Stack
            spacing={4}
            textAlign={"center"}
            component="form"
            onSubmit={handleSubmit(onSubmit2)}
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Full Name" fullWidth />
              )}
            />

            <Controller
              name="clientName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Client Name"
                  fullWidth
                  // InputProps={{
                  //   readOnly: true,
                  // }}
                  disabled
                />
              )}
            />

            <Controller
              name="fullAddress"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Address"
                  fullWidth
                  // InputProps={{
                  //   readOnly: true,
                  // }}
                  disabled
                />
              )}
            />

            <Button variant="contained" type="submit">
              Register
            </Button>
            <Typography>After registering, you will be directed to the login screen. </Typography>
          </Stack>
        )} */}
      </Stack>
    </Container>
  );
};

export default RegisterBox;
